import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobileLayout } from '../../components/layouts/MobileLayout';
import {
    useBenchmarkData,
    useFrontierData,
    useOwnershipData,
    usePeerGroupData,
    usePortfolioData
} from '../../hooks/data';
import { FilterStatesContext } from '../../components/contexts/filterStates';
import DesktopLayout from '../../components/layouts/DesktopLayout';
import FrontierSection from '../../components/sections/FrontierSection';
import { PortfolioDetails } from '../../components/menus/PortfolioDetailsMobile';
import ChartTab from '../../components/tabs/ChartTab';
import SkillsTab from '../../components/tabs/SkillsTab';
import TableTab from '../../components/tabs/TableTab';
import { setComparisonFilters } from '../../functions/filtering';
import PageSection from '../../components/layouts/PageSection';
import Grid from '@mui/material/Grid';
import ProDrillDownHeader from '../../components/proPanels/ProDrillDownHeader';
import Panel from '../../components/Panel';
import { Typography } from '@mui/material';
import SkillsTable from '../../components/tables/SkillsTable';
import { DrillDownScatter } from '../../components/scatterPlots/DrillDownScatter';
import { useFilteredData } from '../../hooks/filtering';
import ProPeerGroupPanelDesktop from '../../components/proPanels/ProPeerGroupPanelDesktop';
import ProScorePanel from '../../components/proPanels/ProScorePanel';
import ProQuartileRank from '../../components/proPanels/ProQuartileRank';
import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import ScoreTab from '../../components/tabs/ScoreTab';
import ProBaScoreOverTime from '../../components/proPanels/ProBaScoreOverTime';
import ProQuartileRankOverTime from '../../components/proPanels/ProQuartileRankOverTime';
import AppPageHeader from '../../components/AppPageHeader';
import Dashboard from '../../components/Dashboard';
import { STATUS } from '../../functions/visibility';
import { formatSkillsData } from '../../functions/dataFormatting';

/** @component
 * Represents the app page for pro users.
 * @returns {Element}
 * @constructor
 */
const ProAppPage = () => {
    const theme = useTheme();
    const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

    const [focusedDot, setFocusedDot] = useState(null); // the focus of pro fields
    const [lastClickedDot, setLastClickedDot] = useState(null);

    const { benchmarkData } = useBenchmarkData();
    const { ownershipData } = useOwnershipData();
    const { frontierData, ebabPeriod } = useFrontierData(
        benchmarkData,
        ownershipData
    );

    const { peerFilterDispatch, setSearchState } =
        useContext(FilterStatesContext);
    const { filteredData } = useFilteredData(frontierData);

    const { responseData: timeSeriesData } = usePortfolioData(
        focusedDot,
        'timeseries'
    );
    const { responseData: crossSectionData } = usePortfolioData(
        focusedDot,
        'cross_section',
        (data) => formatSkillsData(data, theme)
    );

    const { peerGroupData } = usePeerGroupData(timeSeriesData, ebabPeriod);

    const [openPortfolioDetailsDialog, setOpenPortfolioDetailsDialog] =
        useState(false);

    const drillDownRef = useRef();
    const [mobileTab, setMobileTab] = useState(0);
    useEffect(() => {
        // on page load (or filter/search change) find first open dot

        if (filteredData) {
            const firstOpenDot = filteredData.find(
                (dot) => dot.status >= STATUS.open
            );

            setFocusedDot(firstOpenDot);
        }
    }, [filteredData]);

    /**
     * Handle a Frontier tooltip More Details click
     * @param {Object} selectedDot - the dot in question
     */
    function handleMoreDetailsClick(selectedDot) {
        setFocusedDot(selectedDot);

        drillDownRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }

    /**
     * Handle a Mobile Portfolio Details Vs. Peer Group click
     */
    function handleMobileVsPeerGroupClick() {
        setSearchState(null);
        setComparisonFilters(lastClickedDot, peerFilterDispatch);
        setOpenPortfolioDetailsDialog(false);
    }

    if (isBelowSm) {
        return (
            <>
                <MobileLayout
                    mobileTab={mobileTab}
                    setMobileTab={setMobileTab}
                    labels={['Chart', 'Table', 'Score', 'Skills']}
                >
                    <ChartTab
                        data={filteredData}
                        onClick={(event, data) => {
                            event.stopPropagation();
                            setLastClickedDot(data.datum);
                            setOpenPortfolioDetailsDialog(true);
                        }}
                    />
                    <TableTab
                        data={filteredData}
                        onRowClick={(row) => {
                            setLastClickedDot(row);
                            setOpenPortfolioDetailsDialog(true);
                        }}
                    />
                    <ScoreTab
                        selectedPortfolio={focusedDot}
                        peerGroupData={peerGroupData}
                    />
                    <SkillsTab
                        focusedDot={focusedDot}
                        skillsData={crossSectionData}
                    />
                </MobileLayout>
                {lastClickedDot && (
                    <PortfolioDetails
                        portfolioData={lastClickedDot}
                        open={openPortfolioDetailsDialog}
                        handleClose={() => setOpenPortfolioDetailsDialog(false)}
                        onVsPeerGroupClick={handleMobileVsPeerGroupClick}
                        onMoreDetailsClick={() => {
                            setFocusedDot(lastClickedDot);
                            setOpenPortfolioDetailsDialog(false);
                            setMobileTab(2);
                        }}
                        onMoreDetailsDisabled={
                            lastClickedDot?.status < STATUS.open
                        }
                    />
                )}
            </>
        );
    } else {
        return (
            <DesktopLayout>
                <PageSection>
                    {/*Typography and filter button for sm and xs*/}
                    <Grid item xs={12} marginTop={{ xs: '24px', sm: '32px' }}>
                        <AppPageHeader ebabPeriod={ebabPeriod} />
                    </Grid>
                    {/*Dashboard Component (md and above) */}
                    {!isBelowMd && (
                        <Grid item md={12} id='appPageDashboard'>
                            <Dashboard />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FrontierSection
                            data={filteredData}
                            handleMoreDetailsClick={handleMoreDetailsClick}
                            isBasic={false}
                        />
                    </Grid>
                </PageSection>
                <Stack>
                    <Box
                        ref={drillDownRef}
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 50,
                            paddingTop: 105,
                            paddingBottom: 24,
                            backgroundColor: theme.palette.background.default
                        }}
                    >
                        <ProDrillDownHeader
                            selectedDot={focusedDot}
                            peerCount={peerGroupData?.count}
                        />
                    </Box>
                    <PageSection>
                        <Grid item container spacing={2.5}>
                            {!isBelowMd && (
                                <Grid item xs={3}>
                                    <ProPeerGroupPanelDesktop
                                        selectedPortfolio={focusedDot}
                                        peerCount={peerGroupData?.count}
                                    />
                                </Grid>
                            )}
                            <Grid item sm={7.5} md={6}>
                                <ProScorePanel selectedPortfolio={focusedDot} />
                            </Grid>
                            <Grid item sm={4.5} md={3}>
                                <ProQuartileRank
                                    peerGroupData={peerGroupData}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Panel
                                sx={{
                                    padding: '1.5rem',
                                    paddingLeft: '3rem',
                                    paddingTop: '2.5rem',
                                    paddingBottom: 0
                                }}
                            >
                                <Grid
                                    container
                                    spacing={{ sm: 1, md: 2, lg: 3 }}
                                >
                                    <Grid
                                        item
                                        sm={5}
                                        md={6}
                                        id='skillPanelTable'
                                        sx={{ marginBottom: 6 }}
                                    >
                                        <Stack spacing={2}>
                                            <Typography variant='h2'>
                                                Breakdown by Skill
                                            </Typography>
                                            <SkillsTable
                                                data={crossSectionData}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid
                                        item
                                        sm={7}
                                        md={6}
                                        id='skillPanelScatterPlot'
                                        display={'flex'}
                                        alignItems={'center'}
                                        justifyContent={'center'}
                                    >
                                        <DrillDownScatter
                                            data={crossSectionData}
                                        />
                                    </Grid>
                                </Grid>
                            </Panel>
                        </Grid>
                        <Grid item xs={12}>
                            <ProQuartileRankOverTime
                                overTimeData={timeSeriesData}
                                currentPeriod={ebabPeriod}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ProBaScoreOverTime
                                overTimeData={timeSeriesData}
                                portfolioName={focusedDot?.portfolioLongName}
                                currentPeriod={ebabPeriod}
                            />
                        </Grid>
                    </PageSection>
                </Stack>
            </DesktopLayout>
        );
    }
};

export default ProAppPage;
